/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useRef, useCallback } from 'preact/hooks';

type LongPressOptions = {
  isDisabled?: boolean;
  minimumTime?: number;
};

type LongPressInfo = {
  x: number;
  y: number;
};

type LongPressState = {
  pointerDownId: number | null;
  startTime: number;
};

const InitialPressState = {
  pointerDownId: null,
  startTime: 0
};

const DefaultMinimumTime = 250;

export function useLongPress(
  onLongPress: (e: LongPressInfo) => void,
  { isDisabled = false, minimumTime = DefaultMinimumTime }: LongPressOptions = {
    isDisabled: false,
    minimumTime: DefaultMinimumTime
  }
): { longPressProps: Record<string, any> } {
  const ref = useRef<LongPressState>(InitialPressState);

  const onPointerDown = useCallback((e: PointerEvent) => {
    // return if it is not a left click
    if (e.pointerType == 'mouse' && e.button != 0) {
      return;
    }

    ref.current = ref.current.pointerDownId
      ? InitialPressState
      : { pointerDownId: e.pointerId, startTime: e.timeStamp };
  }, []);

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      if (e.pointerId === ref.current.pointerDownId) {
        const longPressTime = e.timeStamp - ref.current.startTime;
        // 250ms minimum longPress time based on numbers used in Hammer
        if (longPressTime > minimumTime) {
          onLongPress({ x: e.offsetX, y: e.offsetY });
        }
      }
      clearLongPress();
    },
    [onLongPress]
  );

  const clearLongPress = useCallback(() => {
    ref.current = InitialPressState;
  }, []);

  const longPressProps = isDisabled
    ? {}
    : {
        onPointerDown,
        onPointerUp,
        onPointerLeave: clearLongPress,
        onPointerCancel: clearLongPress
      };

  return {
    longPressProps
  };
}
